import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export type RosterProvider = 'alayacare' | 'shiftcare' | 'brevity' | 'procura' | 'visualcare';

export interface CredentialField {
  key: 'api_token' | 'api_public_key' | 'api_private_key';
  label: string;
  secret: boolean;
  help_text?: string;
}

export interface RosterProviderDto {
  provider: RosterProvider;
  display_name: string;
  supports_pull: boolean;
  supports_webhook: boolean;
  supports_sqs: boolean;
  credential_fields: CredentialField[];
}

export interface RosterIntegrationDto {
  id: string;
  organisation_id: string;
  provider: RosterProvider;
  display_name: string | null;
  active: boolean;
  webhook_url: string;
  has_api_token: boolean;
  has_api_public_key: boolean;
  has_api_private_key: boolean;
  has_webhook_signing_secret: boolean;
  has_sqs_queue_url: boolean;
  has_sqs_key: boolean;
  has_sqs_secret: boolean;
  api_base_url: string | null;
  api_public_key: string | null;
  sqs_queue_url: string | null;
  sqs_region: string | null;
  sqs_key: string | null;
  extras: Record<string, unknown>;
  created_at: string;
  verified_at: string | null;
}

export interface UpsertRosterInput {
  provider: RosterProvider;
  display_name?: string;
  api_base_url?: string | null;
  api_token?: string | null;
  api_public_key?: string | null;
  api_private_key?: string | null;
  webhook_signing_secret?: string;
  sqs_queue_url?: string | null;
  sqs_region?: string | null;
  sqs_key?: string | null;
  sqs_secret?: string | null;
  extras?: Record<string, unknown>;
  active?: boolean;
}

export interface SyncInput {
  provider: RosterProvider;
  from?: string;
  to?: string;
}

export interface ExternalIdentityDto {
  id: string;
  user_id: string;
  organisation_id: string;
  provider: string;
  external_user_id: string;
  config: Record<string, unknown>;
  sync_status: string;
  linked_at: string;
  // Populated when the adapter supports resolveExternalIdentity. Lets the UI confirm
  // the resolver ran + show what the sync will filter on.
  resolved_display_name?: string | null;
  resolve_error?: string;
}

export function useRosterProviders() {
  const api = useApi();
  return useQuery({
    queryKey: ['roster', 'providers'],
    queryFn: () => api<{ providers: RosterProviderDto[] }>('/roster/providers'),
    staleTime: 5 * 60_000,
  });
}

export function useRosterIntegrations() {
  const api = useApi();
  return useQuery({
    queryKey: ['roster', 'integrations'],
    queryFn: () => api<{ integrations: RosterIntegrationDto[] }>('/roster/integrations'),
    staleTime: 30_000,
  });
}

export function useUpsertRoster() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertRosterInput) =>
      api<{ integration: RosterIntegrationDto }>('/roster/integrations', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roster'] }),
  });
}

export function useDeleteRoster() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/roster/integrations/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roster'] }),
  });
}

export interface SyncTraceEntry {
  url: string;
  status: number;
  returned_count: number;
  total_pages: number | null;
}

export interface SyncResult {
  polled: number;
  fetched: number;
  applied: number;
  skipped: number;
  trace: SyncTraceEntry[];
}

export function useRosterSync() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SyncInput) =>
      api<SyncResult>('/roster/sync', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['visits'] }),
  });
}

export function useUserExternalIdentities(userId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['external-identities', userId],
    queryFn: () =>
      api<{ identities: ExternalIdentityDto[] }>(`/users/${userId}/external-identities`),
    enabled: Boolean(userId),
    staleTime: 30_000,
  });
}

export function useLinkExternalIdentity() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      provider,
      external_user_id,
    }: {
      userId: string;
      provider: RosterProvider;
      external_user_id: string;
    }) =>
      api<{ identity: ExternalIdentityDto }>(`/users/${userId}/external-identities`, {
        method: 'POST',
        body: JSON.stringify({ provider, external_user_id }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['external-identities'] }),
  });
}

export function useUnlinkExternalIdentity() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, provider }: { userId: string; provider: RosterProvider }) =>
      api<void>(`/users/${userId}/external-identities/${provider}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['external-identities'] }),
  });
}
